import { createContext, useContext } from 'react'

export type PatientPanelType = 'newEncounterSoap' | 'careCoordinator'

export type PatientPanelState = {
  open: boolean
  panelType?: PatientPanelType
  payload?: Record<string, unknown>
}

export type ChartRefreshTarget =
  | { kind: 'reference'; reference: string }
  | { kind: 'newestInTab'; dataKey: string }

export type PatientSidePanelContextValue = {
  panelState: PatientPanelState
  openPanel: (panel: { panelType: PatientPanelType; payload?: Record<string, unknown> }) => void
  closePanel: () => void
  replacePanelContent: (panel: {
    panelType: PatientPanelType
    payload?: Record<string, unknown>
  }) => void
  emitPanelEvent: (eventName: string, refreshTarget?: ChartRefreshTarget) => void
  panelEventVersions: Record<string, number>
  refreshTarget?: ChartRefreshTarget
  clearRefreshTarget: () => void
  /**
   * Whether the floating overlay panel is expanded to fill the chart's
   * content width. Owned by `<PatientChartSidePanelHost>` so the action bar
   * and the chart layout (which both reserve right-padding around the
   * overlay) can read a single source of truth.
   */
  isPanelFullWidth: boolean
  setPanelFullWidth: (next: boolean) => void
}

export const PatientSidePanelContext = createContext<PatientSidePanelContextValue | null>(null)

const noop = () => {}

const noopContext: PatientSidePanelContextValue = {
  panelState: { open: false },
  openPanel: noop,
  closePanel: noop,
  replacePanelContent: noop,
  emitPanelEvent: noop,
  panelEventVersions: {},
  clearRefreshTarget: noop,
  isPanelFullWidth: false,
  setPanelFullWidth: noop,
}

export function usePatientSidePanel(): PatientSidePanelContextValue {
  return useContext(PatientSidePanelContext) ?? noopContext
}
